/**
 * MAIN.JS — Cytific KB Tester (Cytific Labs)
 * =================================================
 * Core application entry point. Orchestrates:
 *
 * IMPORTS (lines ~1-40):
 *   - UI Components: Button, MetricCard, TerminalLog, KeyboardKey, Modal, etc.
 *   - Services: KeyboardService, TypingTestService, AccuracyService, ChatterService,
 *               AudioService, NKROService, HeatmapService, ExportService, StuckKeyService,
 *               LockKeyStateService, RepeatRateService, LayoutWizardService
 *
 * LAYOUT DATA (search "Physical layout definitions"):
 *   - Keyboard row definitions for Full, TKL, 75%, 65%, 60% layouts
 * LAYOUT DATA (search "Physical layout definitions"):
 *   - Keyboard row definitions for Full, TKL, 75%, 65%, 60% layouts
 *   - International standard layout character maps (QWERTY, AZERTY, Dvorak, QWERTZ, etc.) imported from LayoutData.js
 *
 * initDashboard() — Main init function:
 *   - Builds the DOM (header, metric cards, ad slot, keyboard stage, side panels)
 *   - Sets up the keyboard toolbar (Style, Layout, Standard Layout, Sound dropdowns)
 *   - Creates keyboard keys and binds them to KeyboardService events
 *   - Handles responsive scaling via updateScaling()
 *   - Wires up all service event listeners (chatter, accuracy, NKRO, typing test, etc.)
 *   - Manages focus mode, heatmap overlay, and export functionality
 */
import { injectTokens } from './theme/index.js';
import { createButton } from './components/Button/index.js';
import { createMetricCard } from './components/MetricCard/index.js';
import { createTerminalLog } from './components/TerminalLog/index.js';
import { createAdSlot } from './components/AdSlot/index.js';
import { createKeyboardKey } from './components/KeyboardKey/index.js';
import { createModal } from './components/Modal/index.js';
import { createSparkline } from './components/Sparkline/index.js';
import { createInfoSection } from './components/InfoSection/index.js';
import { createTypingTestWidget } from './components/TypingTestWidget/index.js';
import { showInterstitialAd } from './components/InterstitialAd/index.js';
// Services
import { keyboardService } from './services/KeyboardService.js';
import { typingTestService } from './services/TypingTestService.js';
import { accuracyService } from './services/AccuracyService.js';
import { chatterService } from './services/ChatterService.js';
import { audioService } from './services/AudioService.js';
import { nkroService } from './services/NKROService.js';
import { heatmapService } from './services/HeatmapService.js';
import { exportService } from './services/ExportService.js';
import { stuckKeyService } from './services/StuckKeyService.js';
import { lockKeyStateService } from './services/LockKeyStateService.js';
import { repeatRateService } from './services/RepeatRateService.js';
import { layoutWizardService } from './services/LayoutWizardService.js';
import { analyticsService } from './services/AnalyticsService.js';
import { apmService } from './services/ApmService.js';
/**
 * @file main.js
 * @description Core entry point for the Cytific KB Tester application.
 * This script orchestrates DOM initialization, service binding, and global event handling.
 * 
 * @module Main
 * @version 1.4.2
 * @author Cytific Labs
 */

import { i18n, t } from './services/i18n.js';
import { standardLayoutMaps } from './config/LayoutData.js';

// Component Styles
import './components/Button/index.css';
import './components/Card/index.css';
import './components/MetricCard/index.css';
import './components/TerminalLog/index.css';
import './components/AdSlot/index.css';
import './components/KeyboardKey/index.css';
import './components/Modal/index.css';
import './components/Sparkline/index.css';
import './components/InfoSection/index.css';
import './components/TypingTestWidget/index.css';
import './components/InterstitialAd/index.css';
import './styles/dashboard.css';

/**
 * Helper to create a labeled select dropdown group with consistent styling.
 * 
 * @param {string} labelText - The text for the <label> element.
 * @param {string} id - The ID for the <select> element.
 * @param {Array<{value: string, text: string, disabled?: boolean}>} options - Array of option objects.
 * @param {Object} [config] - Configuration overrides.
 * @param {boolean} [config.showLabel=true] - Whether to render the label element.
 * @returns {{group: HTMLElement, select: HTMLSelectElement}}
 */
const createSelectGroup = (labelText, id, options, config = { showLabel: true }) => {
  const group = document.createElement('div');
  group.className = 'cm-setting-group';
  const select = document.createElement('select');
  select.id = id;
  options.forEach(({ value, text, disabled }) => {
    const option = document.createElement('option');
    option.value = value;
    option.textContent = text;
    if (disabled) option.disabled = true;
    select.appendChild(option);
  });

  if (config.showLabel) {
    const label = document.createElement('label');
    label.textContent = labelText;
    group.appendChild(label);
  } else {
    group.classList.add('cm-setting-group--no-label');
  }

  group.appendChild(select);
  return { group, select };
};

/**
 * Global Tooltip Manager — Prevents clipping by appending a single tooltip directly to document.body
 * Auto-migrates any element with 'title' or 'data-tooltip' attributes on hover.
 */
class TooltipManager {
  constructor() {
    this.el = document.createElement('div');
    this.el.id = 'cm-global-tooltip';
    this.el.className = 'cm-global-tooltip';
    document.body.appendChild(this.el);

    document.body.addEventListener('mouseover', this.handleMouseOver.bind(this));
    document.body.addEventListener('mouseout', this.handleMouseOut.bind(this));
    document.body.addEventListener('focusin', this.handleMouseOver.bind(this));
    document.body.addEventListener('focusout', this.handleMouseOut.bind(this));

    // Hide on scroll to prevent detached tooltips
    document.addEventListener('scroll', this.handleMouseOut.bind(this), { capture: true, passive: true });
  }

  handleMouseOver(e) {
    const target = e.target.closest('[data-cm-tooltip], [title], [data-tooltip]');
    if (!target) return;

    // Auto-migrate to suppress native browser tooltip
    if (target.hasAttribute('title')) {
      target.setAttribute('data-cm-tooltip', target.getAttribute('title'));
      target.removeAttribute('title');
    }
    if (target.hasAttribute('data-tooltip')) {
      target.setAttribute('data-cm-tooltip', target.getAttribute('data-tooltip'));
      target.removeAttribute('data-tooltip');
    }

    const text = target.getAttribute('data-cm-tooltip');
    if (!text) return;

    this.el.textContent = text;
    this.el.classList.add('is-visible');

    // Measure and smart-position
    const rect = target.getBoundingClientRect();
    const tooltipRect = this.el.getBoundingClientRect();

    let top = rect.top - tooltipRect.height - 10;
    let left = rect.left + (rect.width / 2) - (tooltipRect.width / 2);

    // Viewport boundaries
    if (top < 10) top = rect.bottom + 10; // Flip below if hitting top edge
    if (left < 10) left = 10; // Snap to left edge
    if (left + tooltipRect.width > window.innerWidth - 10) left = window.innerWidth - tooltipRect.width - 10; // Snap right

    this.el.style.top = `${top}px`;
    this.el.style.left = `${left}px`;
  }

  handleMouseOut() {
    this.el.classList.remove('is-visible');
  }
}

/**
 * Main application initialization sequence.
 * 1. Validates DOM mount point (#app).
 * 2. Injects design tokens and initializes privacy infrastructure.
 * 3. Builds the HUD Header, Metrics Bar, and Workspace Grid.
 * 4. Mounts services (NKRO, Accuracy, Keyboard, Audio).
 * 5. Binds global event listeners for keyboard and window events.
 */
const initApp = () => {
  try {
    const app = document.getElementById('app');
    if (!app) {
      console.error('CRITICAL: Element #app not found!');
      return;
    }

    // Initialize global tooltip system first
    window.cmTooltipManager = new TooltipManager();

    // Initialize Design Tokens
    injectTokens();

    // Ensure session-fresh state for heatmap on page load
    if (heatmapService) heatmapService.clear();

    // Sync HTML lang attribute with current language
    document.documentElement.lang = i18n.currentLang || 'en';

    // Google Consent logic is now handled globally by /scripts/consent.js

    const dashboard = document.createElement('div');
    dashboard.className = 'cm-dashboard';
    dashboard.innerHTML = ``;


    // 1. HUD Header (Status & Lock States)
    const hudHeader = document.createElement('header');
    hudHeader.className = 'cm-hud-header';
    hudHeader.innerHTML = `
      <div class="cm-dashboard-title">
        <h1>${t('APP_TITLE')}</h1>
        <span>${t('APP_VERSION')}</span>
      </div>
      <div class="cm-hud-right">
        <div class="cm-status-pill notranslate" translate="no" id="last-key">${t('LAST_KEY')}: --</div>
        <div class="cm-status-pill notranslate" translate="no" id="focus-status">${t('FOCUS_LABEL')}: ${t('FOCUS_ACTIVE')}</div>
        <div id="lock-states" class="cm-lock-states notranslate" translate="no">
          <div class="cm-lock-pill" id="lock-caps" title="Caps Lock OS state">${t('CAPS_LOCK')}</div>
          <div class="cm-lock-pill" id="lock-num" title="NumLock is an OS-level state. Laptops may report it as ON even without a physical NumPad.">${t('NUM_LOCK')}</div>
          <div class="cm-lock-pill" id="lock-scroll" title="Scroll Lock OS state">${t('SCROLL_LOCK')}</div>
        </div>
        <div id="language-mount" class="cm-language-mount"></div>
      </div>
    `;
    dashboard.appendChild(hudHeader);

    // Language Switcher in Header
    const rootLangs = i18n.getAvailableLanguages().map(code => ({
      value: code, text: i18n.getLanguageName(code)
    }));
    const langBundle = createSelectGroup('', 'language-select', rootLangs, { showLabel: false });
    const langSelect = langBundle.select;
    const langGroup = langBundle.group;
    langSelect.value = i18n.currentLang;
    langSelect.addEventListener('change', (e) => {
      i18n.setLanguage(e.target.value);
    });
    const langMount = hudHeader.querySelector('#language-mount');
    if (langMount) langMount.appendChild(langGroup);

    const focusStatusEl = hudHeader.querySelector('#focus-status');
    const lastKeyEl = hudHeader.querySelector('#last-key');

    // 2. Metrics Bar
    const metricsBar = document.createElement('div');
    metricsBar.className = 'cm-metrics-bar';

    const pollingMetric = createMetricCard({ label: t('METRIC_POLLING'), value: '---', unit: t('UNIT_HZ'), description: t('DESC_POLLING') });
    const latencyMetric = createMetricCard({ label: t('METRIC_LATENCY'), value: '---', unit: t('UNIT_MS'), description: t('DESC_LATENCY') });
    const activeKeysMetric = createMetricCard({ label: t('METRIC_ACTIVE'), value: '0', unit: t('UNIT_KEYS'), description: t('DESC_ACTIVE') });
    const maxKeysMetric = createMetricCard({ label: t('METRIC_MAX_ROLL'), value: '0', unit: t('UNIT_KEYS'), description: t('DESC_MAX_ROLL') });
    const apmMetric = createMetricCard({ label: t('METRIC_APM'), value: '0', unit: t('UNIT_APM'), description: t('DESC_APM') });
    const cpsMetric = createMetricCard({ label: t('METRIC_CPS'), value: '0', unit: t('UNIT_CPS'), description: t('DESC_CPS') });
    const totalPressesMetric = createMetricCard({ label: t('METRIC_TOTAL_PRESSES'), value: '0', unit: t('UNIT_PRESSES'), description: t('DESC_TOTAL_PRESSES') });
    let totalPressCount = 0;

    // Initialize Sparklines
    const pollingSpark = createSparkline({ color: 'var(--color-primary)' });
    const latencySpark = createSparkline({ color: 'rgba(255, 255, 255, 0.5)' });
    const apmSpark = createSparkline({ color: 'var(--color-accent)' });

    pollingMetric.appendChild(pollingSpark);
    latencyMetric.appendChild(latencySpark);
    apmMetric.appendChild(apmSpark);

    metricsBar.appendChild(pollingMetric);
    metricsBar.appendChild(latencyMetric);
    metricsBar.appendChild(activeKeysMetric);
    metricsBar.appendChild(maxKeysMetric);
    metricsBar.appendChild(apmMetric);
    metricsBar.appendChild(cpsMetric);
    metricsBar.appendChild(totalPressesMetric);
    dashboard.appendChild(metricsBar);

    // 2.5 Ad Row (Moved to stage)

    // 3. Workspace Layout (Left / Center / Right)
    const workspace = document.createElement('div');
    workspace.className = 'cm-workspace';

    const leftPanel = document.createElement('aside');
    leftPanel.className = 'cm-panel cm-panel--left';

    const rightPanel = document.createElement('aside');
    rightPanel.className = 'cm-panel cm-panel--right';


    const stage = document.createElement('main');
    stage.className = 'cm-stage';
    stage.innerHTML = `
      <div class="cm-ad-row" style="padding: 0 0 16px 0; background: transparent;" id="stage-ad-mount"></div>
      <div class="cm-kb-toolbar" id="kb-toolbar"></div>
      <div id="keyboard-container" class="cm-keyboard-canvas">
        <div class="cm-keyboard-frame">
          <div class="cm-keyboard-row">
            <div id="keyboard-mount" class="cm-mount-main"></div>
            <div id="nav-mount" class="cm-mount-nav"></div>
            <div id="numpad-mount" class="cm-mount-numpad"></div>
          </div>
        </div>
      </div>
      <button class="cm-focus-badge" type="button" aria-label="Exit focus mode">${t('FOCUS_MODE_BTN')}</button>
    `;
    // Ad slot above the keyboard - strictly clamped to 90px height
    const adMount = stage.querySelector('#stage-ad-mount');
    adMount.innerHTML = `
      <div style="height:90px; max-height:90px; width:100%; max-width:970px; margin:0 auto; display:flex; justify-content:center; align-items:center; position:relative; overflow:hidden; border: 1px dashed rgba(255,255,255,0.08); border-radius: 8px; background: transparent;">
        <span style="font-size:10px; color:rgba(255,255,255,0.15); font-family:'IBM Plex Mono', monospace; letter-spacing: 0.1em;">ADVERTISEMENT</span>
      </div>
    `;
    // ── AdSense Activation ────────────────────────────────────
    // When AdSense is approved and serving ads, replace the placeholder above with:
    //   <ins class="adsbygoogle" style="display:inline-block; width:100%; height:90px;"
    //        data-ad-client="ca-pub-5384626296485560"
    //        data-ad-slot="1234567890"
    //        data-ad-format="horizontal"></ins>
    // And uncomment: (window.adsbygoogle = window.adsbygoogle || []).push({});


    const kbToolbar = stage.querySelector('#kb-toolbar');

    // Left panel: Event log + Accuracy disclosure
    const eventLog = createTerminalLog({ title: t('LOG_TITLE') });
    eventLog.classList.add('cm-terminal--panel');
    eventLog.addLog(t('LOG_RESUMED'));
    const eventBuffer = [];
    const rawAddLog = eventLog.addLog;
    const rawClearLogs = eventLog.clearLogs;
    eventLog.addLog = (text) => {
      eventBuffer.push({ ts: new Date().toISOString(), text });
      if (eventBuffer.length > 500) eventBuffer.shift();
      rawAddLog(text);
    };
    eventLog.clearLogs = () => {
      eventBuffer.length = 0;
      rawClearLogs();
    };

    let lastPeak = 0;
    let chordSplitCount = 0;
    let stuckCount = 0;
    let chatterCount = 0;
    let lastRepeat = null;
    let styleSelect;
    let layoutSelect;
    let soundSelect;
    let standardSelect;
    let updateOsToggle = () => { };
    let setStandardLayout = () => { };

    // Preference Persistence Helpers
    const PREF_KEY = 'cytific_user_prefs';
    const loadUserPrefs = () => {
      try {
        const saved = localStorage.getItem(PREF_KEY);
        return saved ? JSON.parse(saved) : {};
      } catch (e) { return {}; }
    };

    const saveUserPref = (key, value) => {
      const prefs = loadUserPrefs();
      prefs[key] = value;
      localStorage.setItem(PREF_KEY, JSON.stringify(prefs));
    };

    const userPrefs = loadUserPrefs();
    let currentLayout = userPrefs.layout || 'full';
    let currentOs = userPrefs.os || 'win';
    let currentStandard = userPrefs.standard || 'en';
    let currentStyle = userPrefs.style || 'classic';
    let currentSound = userPrefs.sound || 'off';

    const getHeatmapTopKeys = (n = 10) => {
      const entries = [...heatmapService.pressCounts.entries()];
      entries.sort((a, b) => b[1] - a[1]);
      return entries.slice(0, n).map(([key, count], i) => ({ rank: i + 1, key, count }));
    };

    const getSessionDuration = () => {
      const ms = performance.now();
      const totalSec = Math.floor(ms / 1000);
      const m = Math.floor(totalSec / 60);
      const s = totalSec % 60;
      return `${m}m ${s}s`;
    };

    const getTotalPresses = () => {
      let total = 0;
      heatmapService.pressCounts.forEach(v => total += v);
      return total;
    };

    const buildExportPayload = () => {
      const apmData = apmService.getMetrics();
      return {
        app: 'Cytific KB Tester',
        url: 'https://tools.cytificlabs.com/kb-tester',
        timestamp: new Date().toISOString(),
        sessionDuration: getSessionDuration(),
        environment: navigator.userAgent,
        configuration: {
          layout: currentLayout,
          style: currentStyle,
          standard: currentStandard,
          os: currentOs,
          detectedLayout: layoutWizardService.detectedLayout || 'NOT_TESTED'
        },
        performance: {
          apm: apmData.apm,
          cps: apmData.cps,
          maxCps: apmData.maxCps,
          pollingHz: accuracyService.getHz(),
          latencyPeakMs: lastPeak,
          maxSimultaneous: keyboardService.getMaxSimultaneous()
        },
        diagnostics: {
          chatterCount,
          stuckCount,
          chordSplitCount,
          repeatRate: lastRepeat
        },
        heatmap: {
          totalPresses: getTotalPresses(),
          uniqueKeysUsed: heatmapService.pressCounts.size,
          topKeys: getHeatmapTopKeys(10)
        },
        nkroResults: Array.isArray(nkroService.results) ? nkroService.results : [],
        history: keyboardService.getHistoryKeys(),
        chatterLog: chatterService.getLogs(),
        eventLog: eventBuffer
      };
    };

    const buildReportPayload = () => {
      const apmData = apmService.getMetrics();
      return {
        appName: 'Cytific KB Tester',
        url: 'https://tools.cytificlabs.com/kb-tester',
        timestamp: new Date().toLocaleString(),
        sessionDuration: getSessionDuration(),
        hz: accuracyService.getHz(),
        latencyPeakMs: lastPeak,
        maxSimultaneous: keyboardService.getMaxSimultaneous(),
        apm: apmData.apm,
        cps: apmData.cps,
        maxCps: apmData.maxCps,
        chatterCount,
        stuckCount,
        chordSplitCount,
        totalPresses: getTotalPresses(),
        topKeys: getHeatmapTopKeys(5),
        results: Array.isArray(nkroService.results) ? nkroService.results : [],
        layout: currentLayout,
        style: currentStyle,
        standard: currentStandard,
        environment: navigator.userAgent
      };
    };

    const eventSection = document.createElement('section');
    eventSection.className = 'cm-panel-section cm-panel-section--grow';
    eventSection.appendChild(eventLog);

    const accuracySection = document.createElement('section');
    accuracySection.className = 'cm-panel-section';
    accuracySection.innerHTML = `
      <div class="cm-panel-title">${t('ACC_TITLE', 'ACCURACY_LIMITS')}</div>
      <p class="cm-accuracy-note">
        ${t('ACC_DESC', 'Polling/latency metrics are browser estimates. Use them to compare keyboards (A vs B), not as raw USB truth. NKRO and key visibility are highly reliable.')}
      </p>
      <div class="cm-accuracy-tags">
        <span class="cm-tag">${t('ACC_TAG_NKRO', 'NKRO: HIGH')}</span>
        <span class="cm-tag">${t('ACC_TAG_CHAT', 'CHATTER: GOOD')}</span>
        <span class="cm-tag">${t('ACC_TAG_LAT', 'LATENCY: EST')}</span>
      </div>
    `;

    const leftPanelSections = [eventSection, accuracySection];
    leftPanelSections.forEach((section) => leftPanel.appendChild(section));
    window.cytificGetHeatmapColor = (count) => {
      if (count === 0) return '';
      const ratio = Math.min(1, count / 20); // Reaches full heat at 20 presses for more punchy feedback
      const hue = Math.floor((1 - ratio) * 240); // 240 (Blue) -> 0 (Red)
      return `hsla(${hue}, 100%, 50%, ${0.4 + (ratio * 0.6)})`;
    };

    // Toggle Heatmap Layer
    const heatmapBtn = createButton({
      label: 'Heatmap: Off', type: 'outline', onClick: () => {
        const isActive = document.body.classList.toggle('cm-heatmap-view');
        heatmapBtn.textContent = isActive ? t('BTN_HEATMAP_ON', 'Heatmap: On') : t('BTN_HEATMAP_OFF', 'Heatmap: Off');
        heatmapBtn.className = `cm-button cm-button--${isActive ? 'primary' : 'outline'}`;

        if (isActive) {
          Object.entries(keyNodes).forEach(([kc, btn]) => {
            const stats = heatmapService.getKeyStats(kc);
            if (stats.count > 0) {
              btn.style.setProperty('background-color', window.cytificGetHeatmapColor(stats.count), 'important');
              btn.style.setProperty('box-shadow', `0 0 15px ${window.cytificGetHeatmapColor(stats.count)}`, 'important');
            }
          });
        } else {
          Object.values(keyNodes).forEach(btn => {
            btn.style.removeProperty('background-color');
            btn.style.removeProperty('box-shadow');
          });
        }
      }
    });
    heatmapBtn.title = 'Heatmap — Color-code keys by press frequency. Blue = rarely pressed, Red = heavily pressed. Great for ergonomic analysis.';

    const audioBtn = createButton({
      label: t('BTN_AUDIO_OFF', 'Sound: Off'), type: 'outline', onClick: () => {
        audioService.init();
        const muted = audioService.toggleMute();
        audioBtn.textContent = muted ? t('BTN_AUDIO_OFF', 'Sound: Off') : t('BTN_AUDIO_ON', 'Sound: On');
        audioBtn.className = `cm-button cm-button--${muted ? 'outline' : 'primary'}`;
        if (soundSelect) {
          soundSelect.value = muted ? 'off' : audioService.currentPreset;
        }
      }
    });
    audioBtn.title = 'Sound Effects — Toggle key press sounds. Choose from 15+ sound profiles in the toolbar (THOCK, clicky, tactile, etc.).';

    const resetSession = () => {
      keyboardService.clearAll();
      accuracyService.reset();
      chatterService.reset();
      repeatRateService.reset();
      stuckKeyService.clearAll();
      heatmapService.clear();
      apmService.clear();
      Object.values(keyNodes).forEach((node) => {
        node.style.boxShadow = '';
        node.style.backgroundColor = '';
        const countEl = node.querySelector('.cm-key__count');
        if (countEl) countEl.textContent = '';
      });
      eventLog.clearLogs();
      eventLog.addLog('SESSION_RESET');
      pollingMetric.querySelector('.cm-metric-card__value').textContent = '---';
      latencyMetric.querySelector('.cm-metric-card__value').textContent = '---';
      activeKeysMetric.querySelector('.cm-metric-card__value').textContent = '0';
      maxKeysMetric.querySelector('.cm-metric-card__value').textContent = '0';
      apmMetric.querySelector('.cm-metric-card__value').textContent = '0';
      cpsMetric.querySelector('.cm-metric-card__value').textContent = '0';
      totalPressCount = 0;
      totalPressesMetric.querySelector('.cm-metric-card__value').textContent = '0';
      chatterCount = 0;
      stuckCount = 0;
      chordSplitCount = 0;
      lastRepeat = null;
      lastPeak = 0;
      updateDiagCounts();
      const diagMount = document.getElementById('diag-mount');
      if (diagMount) {
        diagMount.innerHTML = `<span class="cm-diag-status__dot"></span><span class="cm-diag-status__label">${t('SYS_STABLE', 'SYSTEM_STABLE')}</span>`;
        diagMount.classList.remove('cm-diag-status--error', 'cm-diag-status--accent');
      }
      if (lastKeyEl) {
        lastKeyEl.textContent = t('LAST_KEY_NULL', 'LAST: --');
      }
      document.body.classList.remove('cm-heatmap-view');
      heatmapBtn.textContent = t('BTN_HEATMAP_OFF', 'Heatmap: Off');
      heatmapBtn.className = 'cm-button cm-button--outline';
      Object.values(keyNodes).forEach(btn => {
        btn.style.removeProperty('background-color');
        btn.style.removeProperty('box-shadow');
      });
    };

    const resetBtn = createButton({ label: 'RESET', type: 'outline', onClick: resetSession });
    resetBtn.title = 'Reset Session — Clear all metrics, event logs, and key states. Start a fresh testing session.';
    const resetHint = document.createElement('span');
    resetHint.className = 'cm-reset-hint';
    resetHint.textContent = 'Ctrl+⌫';
    resetBtn.appendChild(resetHint);

    // Hotkey: Ctrl + Backspace to reset session
    window.addEventListener('keydown', (e) => {
      if (e.ctrlKey && e.code === 'Backspace') {
        e.preventDefault();
        e.stopPropagation();
        resetSession();
      }
    }, true);

    // Right panel: Controls + Diagnostics + Settings + Reports
    const controlSection = document.createElement('section');
    controlSection.className = 'cm-panel-section';
    controlSection.innerHTML = `<div class="cm-panel-title">CONTROL_DECK</div>`;

    const setFocusMode = (enabled) => {
      const isActive = Boolean(enabled);
      document.body.classList.toggle('cm-focus-mode', isActive);
      focusBtn.textContent = isActive ? t('BTN_FOCUS_ON', 'Focus: On') : t('BTN_FOCUS_OFF', 'Focus: Off');
      focusBtn.className = `cm-button cm-button--${isActive ? 'primary' : 'outline'}`;
    };

    const focusBtn = createButton({
      label: t('BTN_FOCUS_OFF', 'Focus: Off'), type: 'outline', onClick: () => {
        const isActive = !document.body.classList.contains('cm-focus-mode');
        setFocusMode(isActive);
      }
    });
    focusBtn.title = t('TIP_FOCUS', 'Dims side panels for focus. Press Esc to exit focus mode.');

    const controlGrid = document.createElement('div');
    controlGrid.className = 'cm-control-grid';
    controlGrid.appendChild(heatmapBtn);
    controlGrid.appendChild(focusBtn);
    controlGrid.appendChild(audioBtn);
    controlGrid.appendChild(resetBtn);
    controlSection.appendChild(controlGrid);


    const optionsBar = document.createElement('div');
    optionsBar.className = 'cm-kb-toolbar__row';
    const optionsLeft = document.createElement('div');
    optionsLeft.className = 'cm-kb-toolbar__left';
    const optionsRight = document.createElement('div');
    optionsRight.className = 'cm-kb-toolbar__right';

    const styleBundle = createSelectGroup(t('UI_STYLE'), 'style-select', [
      { value: 'classic', text: 'CLASSIC (DEFAULT)' },
      { value: 'cyber', text: 'CYBER' },
      { value: 'synthwave', text: 'SYNTHWAVE' },
      { value: 'mint', text: 'MINT' },
      { value: 'dracula', text: 'DRACULA' },
      { value: 'nord', text: 'NORD' },
      { value: 'floating', text: 'FLOATING' },
      { value: 'retro', text: 'RETRO' },
      { value: 'flat', text: 'FLAT' },
      { value: 'bubble', text: 'BUBBLE (ROUNDED)' },
      { value: 'mac', text: 'MAC (SILVER/WHITE)' },
      { value: 'arcade', text: 'ARCADE (CHUNKY)' }
    ]);
    styleSelect = styleBundle.select;
    const styleGroup = styleBundle.group;

    const layoutBundle = createSelectGroup(t('UI_LAYOUT'), 'layout-select', [
      { value: 'full', text: t('LO_FULL', 'Compact full sized') },
      { value: 'tkl', text: t('LO_TKL', 'Compact tenkeyless keyboard') },
      { value: '75', text: t('LO_75', 'Compact 75%') },
      { value: '65', text: t('LO_65', 'Compact 65%') },
      { value: '60', text: t('LO_60', 'Compact keyboard') }
    ]);
    layoutSelect = layoutBundle.select;
    const layoutGroup = layoutBundle.group;
    layoutGroup.classList.add('cm-setting-group--wide');

    const standardBundle = createSelectGroup(t('UI_STANDARD'), 'standard-layout-select', [
      { value: 'en', text: t('STD_EN', 'US English layout') },
      { value: 'en-gb', text: t('STD_EN_GB', 'UK English layout') },
      { value: 'en-azerty', text: t('STD_EN_AZERTY', 'English layout (Azerty)') },
      { value: 'en-dvorak', text: t('STD_EN_DVORAK', 'English layout (Dvorak)') },
      { value: 'es', text: t('STD_ES', 'Distribucion en espanol') },
      { value: 'fr', text: t('STD_FR', 'Disposition francaise') },
      { value: 'de', text: t('STD_DE', 'Layout deutsche') },
      { value: 'it', text: t('STD_IT', 'Layout italiano') },
      { value: 'nl', text: t('STD_NL', 'Nederlandse lay-out') },
      { value: 'pt-br', text: t('STD_PT_BR', 'Layout em portugues (Brasil)') },
      { value: 'uk', text: t('STD_UK', 'Ukrainska rozkladka') },
      { value: 'ru', text: t('STD_RU', 'Русская раскладка') },
      { value: 'pl', text: t('STD_PL', 'Uklad polski') },
      { value: 'tr', text: t('STD_TR', 'Turkce duzen') },
      { value: 'ko', text: t('STD_KO', '한글 키보드 (Korean)') },
      { value: 'ar', text: t('STD_AR', 'لوحة المفاتيح العربية (Arabic)') },
      { value: 'ch', text: t('STD_CH', 'Swiss QWERTZ') }
    ]);
    standardSelect = standardBundle.select;
    const standardGroup = standardBundle.group;
    standardGroup.classList.add('cm-setting-group--wide');

    const soundBundle = createSelectGroup(t('UI_SOUND'), 'sound-preset', [
      { value: 'off', text: t('SND_OFF', 'Sound: Off') },
      { value: 'thock', text: t('SND_THOCK', 'THOCK (CLASSIC)') },
      { value: 'creamy', text: t('SND_CREAMY', 'CREAMY (DEEP)') },
      { value: 'clicky', text: t('SND_CLICKY', 'CLICKY (CRISP)') },
      { value: 'tactile', text: t('SND_TACTILE', 'TACTILE (BUMP)') },
      { value: 'blue_switch', text: t('SND_BLUE', 'MX BLUE (LOUD)') },
      { value: 'brown_switch', text: t('SND_BROWN', 'MX BROWN') },
      { value: 'red_switch', text: t('SND_RED', 'MX RED (LINEAR)') },
      { value: 'typewriter', text: t('SND_TYPE', 'TYPEWRITER') },
      { value: 'membrane', text: t('SND_MEMB', 'MEMBRANE (SOFT)') },
      { value: 'silent', text: t('SND_SILENT', 'SILENT (MUTED)') },
      { value: 'bubble_pop', text: t('SND_BUBBLE', 'BUBBLE POP') },
      { value: 'bit8', text: t('SND_BIT8', '8-BIT ARCADE') },
      { value: 'laser', text: t('SND_LASER', 'CYBER LASER') },
      { value: 'wooden', text: t('SND_WOOD', 'WOODEN THUD') },
      { value: 'steel', text: t('SND_STEEL', 'STEEL PLATE') }
    ]);
    soundSelect = soundBundle.select;
    const soundGroup = soundBundle.group;

    const refreshBtn = document.createElement('button');
    refreshBtn.type = 'button';
    refreshBtn.className = 'cm-kb-icon';
    refreshBtn.title = t('BTN_RESET', 'Reset');
    refreshBtn.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>`;
    refreshBtn.addEventListener('click', resetSession);

    const iconFull = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/></svg>`;
    const iconExit = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"/></svg>`;
    const fullscreenBtn = document.createElement('button');
    fullscreenBtn.type = 'button';
    fullscreenBtn.className = 'cm-kb-icon';
    fullscreenBtn.title = t('BTN_FULLSCREEN', 'Toggle Fullscreen');
    fullscreenBtn.innerHTML = iconFull;
    fullscreenBtn.addEventListener('click', async () => {
      try {
        if (document.fullscreenElement) {
          await document.exitFullscreen();
        } else {
          await document.documentElement.requestFullscreen();
        }
      } catch (err) {
        eventLog.addLog('FULLSCREEN_NOT_ALLOWED');
      }
    });
    const updateFullscreenBtn = () => {
      fullscreenBtn.innerHTML = document.fullscreenElement ? iconExit : iconFull;
    };
    document.addEventListener('fullscreenchange', updateFullscreenBtn);

    const osToggle = document.createElement('div');
    osToggle.className = 'cm-os-toggle';
    const osMacBtn = document.createElement('button');
    osMacBtn.type = 'button';
    osMacBtn.title = 'macOS';
    osMacBtn.className = 'cm-os-toggle__btn';
    osMacBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24"><path d="M18 3a3 3 0 0 0-3 3v12a3 3 0 0 0 3 3 3 3 0 0 0 3-3 3 3 0 0 0-3-3H6a3 3 0 0 0-3 3 3 3 0 0 0 3 3 3 3 0 0 0 3-3V6a3 3 0 0 0-3-3 3 3 0 0 0-3 3 3 3 0 0 0 3 3h12a3 3 0 0 0 3-3 3 3 0 0 0-3-3z"/></svg>`;

    const osWinBtn = document.createElement('button');
    osWinBtn.type = 'button';
    osWinBtn.title = 'Windows';
    osWinBtn.className = 'cm-os-toggle__btn is-active';
    osWinBtn.innerHTML = `<svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.949-1.801"/></svg>`;

    updateOsToggle = (mode) => {
      osMacBtn.classList.toggle('is-active', mode === 'mac');
      osWinBtn.classList.toggle('is-active', mode === 'win');

      // Explicitly set colors for active state
      osMacBtn.style.color = mode === 'mac' ? 'var(--color-primary)' : 'var(--color-text-dim)';
      osWinBtn.style.color = mode === 'win' ? 'var(--color-primary)' : 'var(--color-text-dim)';
    };

    osMacBtn.addEventListener('click', () => setOsMode('mac'));
    osWinBtn.addEventListener('click', () => setOsMode('win'));

    osToggle.appendChild(osMacBtn);
    osToggle.appendChild(osWinBtn);

    const shareIconBtn = document.createElement('button');
    shareIconBtn.type = 'button';
    shareIconBtn.className = 'cm-kb-icon';
    shareIconBtn.title = 'Share Results — Generate a branded PNG card of your keyboard stats';
    shareIconBtn.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>`;
    shareIconBtn.addEventListener('click', () => {
      exportService.exportShareCard(buildReportPayload());
    });

    const privacyIconBtn = document.createElement('button');
    privacyIconBtn.type = 'button';
    privacyIconBtn.className = 'cm-kb-icon';
    privacyIconBtn.title = 'Privacy & Cookies — Manage your consent preferences';
    privacyIconBtn.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><circle cx="12" cy="11" r="3"/></svg>`;
    privacyIconBtn.addEventListener('click', () => {
      if (window.CytificConsent && window.CytificConsent.showPrivacyCenter) window.CytificConsent.showPrivacyCenter();
    });

    optionsLeft.appendChild(refreshBtn);
    optionsLeft.appendChild(fullscreenBtn);
    optionsLeft.appendChild(shareIconBtn);
    optionsLeft.appendChild(privacyIconBtn);
    optionsLeft.appendChild(osToggle);

    optionsRight.appendChild(styleGroup);
    optionsRight.appendChild(layoutGroup);
    optionsRight.appendChild(standardGroup);
    optionsRight.appendChild(soundGroup);

    optionsBar.appendChild(optionsLeft);
    optionsBar.appendChild(optionsRight);
    if (kbToolbar) kbToolbar.appendChild(optionsBar);

    const styleClasses = ['cm-style-cyber', 'cm-style-classic', 'cm-style-synthwave', 'cm-style-mint', 'cm-style-dracula', 'cm-style-nord', 'cm-style-floating', 'cm-style-retro', 'cm-style-flat', 'cm-style-bubble', 'cm-style-mac', 'cm-style-arcade'];
    const LIGHT_THEMES = ['mint', 'retro', 'bubble', 'mac'];
    const applyStylePreset = (styleKey) => {
      // Sync both <html> and <body> to prevent CSS variable inheritance conflicts.
      // The Theme Guard (index.html) sets the initial class on <html> for FOUC prevention.
      // We must clear it here too, or the old theme's variables persist on the root element.
      styleClasses.forEach((cls) => {
        document.documentElement.classList.remove(cls);
        document.body.classList.remove(cls);
      });
      document.documentElement.classList.add(`cm-style-${styleKey}`);
      document.body.classList.add(`cm-style-${styleKey}`);

      // Generic light/dark theme marker — CSS uses .cm-theme-light for all overrides
      const isLight = LIGHT_THEMES.includes(styleKey);
      document.documentElement.classList.toggle('cm-theme-light', isLight);
      document.body.classList.toggle('cm-theme-light', isLight);

      currentStyle = styleKey;
      saveUserPref('style', styleKey);

      // Audio Theme Sync
      if (!audioService.mute) {
        const suggested = audioService.syncWithTheme(styleKey);
        if (soundSelect) {
          soundSelect.value = suggested;
          saveUserPref('sound', suggested);
        }
      }
      analyticsService.trackEvent('style_change', { style: styleKey });
    };

    const setLayoutMode = (layoutKey) => {
      const safeLayout = layoutPresets && layoutPresets[layoutKey] ? layoutKey : 'full';
      currentLayout = safeLayout;
      if (layoutSelect) layoutSelect.value = safeLayout;
      if (typeof renderLayout === 'function') {
        renderLayout(safeLayout);
      }
      saveUserPref('layout', safeLayout);
      analyticsService.trackEvent('layout_change', { layout: safeLayout });
      eventLog.addLog(`LAYOUT_SET: ${safeLayout.toUpperCase()}`);
    };

    const setOsMode = (mode) => {
      currentOs = mode;
      if (typeof applyOsLabels === 'function') {
        applyOsLabels();
      }
      updateOsToggle(mode);
      saveUserPref('os', mode);
      eventLog.addLog(`OS_MODE: ${mode.toUpperCase()}`);
    };

    styleSelect.addEventListener('change', (e) => { applyStylePreset(e.target.value); e.target.blur(); });

    layoutSelect.addEventListener('change', (e) => { setLayoutMode(e.target.value); e.target.blur(); });
    standardSelect.addEventListener('change', (e) => {
      setStandardLayout(e.target.value);
      saveUserPref('standard', e.target.value);
      e.target.blur();
    });
    soundSelect.addEventListener('change', (e) => {
      const value = e.target.value;
      audioService.init();
      saveUserPref('sound', value);
      if (value === 'off') {
        audioService.setMuted(true);
        audioBtn.textContent = t('BTN_AUDIO_OFF', 'Sound: Off');
        audioBtn.className = 'cm-button cm-button--outline';
        e.target.blur();
        return;
      }
      audioService.setMuted(false);
      audioService.setPreset(value);
      audioBtn.textContent = t('BTN_AUDIO_ON', 'SOUND_ON');
      audioBtn.className = 'cm-button cm-button--primary';
      audioService.playKeySound('KeyA', 0);
      e.target.blur();
    });

    applyStylePreset(currentStyle);
    styleSelect.value = currentStyle;
    layoutSelect.value = currentLayout;
    standardSelect.value = currentStandard;

    // Initialize standard layout if needed
    if (typeof setStandardLayout === 'function') {
      setStandardLayout(currentStandard);
    }

    // Audio Initialization
    if (currentSound !== 'off') {
      audioService.setPreset(currentSound);
      audioService.setMuted(false);
      audioBtn.textContent = 'SOUND_ON';
      audioBtn.className = 'cm-button cm-button--primary';
    } else {
      audioService.setMuted(true);
      audioBtn.textContent = 'SOUND_OFF';
      audioBtn.className = 'cm-button cm-button--outline';
    }
    soundSelect.value = currentSound;

    updateOsToggle(currentOs);

    const testSection = document.createElement('section');
    testSection.className = 'cm-panel-section';
    testSection.innerHTML = `<div class="cm-panel-title">TESTS_TOOLS</div>`;
    const wizardGrid = document.createElement('div');
    wizardGrid.className = 'cm-control-grid cm-control-grid--two';
    wizardGrid.appendChild(createButton({ label: 'NKRO_TEST', type: 'outline', onClick: () => showInterstitialAd(() => nkroService.startWizard(), { forceShow: true }) }));
    wizardGrid.appendChild(createButton({ label: 'LAYOUT_WIZARD', type: 'outline', onClick: () => showInterstitialAd(() => layoutWizardService.start(), { forceShow: true }) }));

    // Track repeat button to manually apply active styling
    let repeatBtn;
    repeatBtn = createButton({
      label: 'REPEAT_TEST', type: 'outline', onClick: () => showInterstitialAd(() => {
        const isActive = repeatBtn.classList.contains('cm-button--active');
        if (isActive) {
          repeatBtn.classList.remove('cm-button--active');
          const diagMount = document.getElementById('diag-mount');
          if (diagMount) {
            diagMount.innerHTML = `<span class="cm-diag-status__dot"></span><span class="cm-diag-status__label">SYSTEM_STABLE</span>`;
            diagMount.classList.remove('cm-diag-status--error', 'cm-diag-status--accent');
          }
          return;
        }

        repeatBtn.classList.add('cm-button--active');
        repeatRateService.startMeasurement();
        eventLog.addLog('REPEAT_TEST_ARMED');
        const diagMount = document.getElementById('diag-mount');
        if (diagMount) {
          diagMount.textContent = 'HOLD_ANY_KEY_TO_MEASURE_REPEAT';
        }
      }, { forceShow: true })
    });
    wizardGrid.appendChild(repeatBtn);
    testSection.appendChild(wizardGrid);

    const diagSection = document.createElement('section');
    diagSection.className = 'cm-panel-section';
    diagSection.innerHTML = `<div class="cm-panel-title">DIAGNOSTICS</div>`;
    const diagMount = document.createElement('div');
    diagMount.id = 'diag-mount';
    diagMount.className = 'cm-diag-status';
    diagMount.innerHTML = `<span class="cm-diag-status__dot"></span><span class="cm-diag-status__label">SYSTEM_STABLE</span>`;
    diagMount.title = 'System Status — Shows the latest diagnostic event. Green = stable, Red = chatter/stuck key detected.';

    const diagGrid = document.createElement('div');
    diagGrid.className = 'cm-diag-grid';
    diagGrid.innerHTML = `
      <div class="cm-diag-item cm-tooltip-trigger" data-tooltip="Your key sent multiple signals for a single press, indicating hardware wear. High values suggest worn switches or debris." tabindex="0">CHATTER<span id="diag-chatter" class="cm-diag-value">0</span></div>
      <div class="cm-diag-item cm-tooltip-trigger" data-tooltip="A key was detected as held down for an abnormally long time. May indicate a physically stuck key or a faulty mechanical switch." tabindex="0">STUCK<span id="diag-stuck" class="cm-diag-value">0</span></div>
      <div class="cm-diag-item cm-tooltip-trigger" data-tooltip="Rapid simultaneous key releases that separated within a single frame. Common during fast gaming key combos — not usually a defect." tabindex="0">CHORD_SPLIT<span id="diag-chord" class="cm-diag-value">0</span></div>
    `;
    const diagChatterEl = diagGrid.querySelector('#diag-chatter');
    const diagStuckEl = diagGrid.querySelector('#diag-stuck');
    const diagChordEl = diagGrid.querySelector('#diag-chord');
    const updateDiagCounts = () => {
      if (diagChatterEl) diagChatterEl.textContent = String(chatterCount);
      if (diagStuckEl) diagStuckEl.textContent = String(stuckCount);
      if (diagChordEl) diagChordEl.textContent = String(chordSplitCount);

      if (chatterCount > 0) analyticsService.trackEvent('chatter_detected', { count: chatterCount });
    };
    updateDiagCounts();
    diagSection.appendChild(diagMount);
    diagSection.appendChild(diagGrid);

    // Settings block
    const settingsContent = document.createElement('div');
    settingsContent.className = 'cm-settings-grid';
    settingsContent.innerHTML = `
      <div class="cm-setting-group" title="Chatter Gate — Debounce threshold in milliseconds. Keys registering twice within this window are flagged as chatter. Default: 20ms. Lower = more sensitive.">
        <label style="display: flex; justify-content: space-between; align-items: center;">
          <span>CHATTER_GATE: <span id="chatter-val">20</span>ms</span>
          <button id="chatter-reset" type="button" class="cm-kb-icon" style="padding: 2px 6px; font-size: 8px;" title="Reset chatter gate to 20ms default">RESET</button>
        </label>
        <input type="range" id="chatter-range" min="1" max="100" value="20">
      </div>
    `;

    const ttsToggle = createButton({
      label: 'TTS_OFF', type: 'outline', onClick: () => {
        const enabled = audioService.toggleTTS();
        ttsToggle.textContent = enabled ? 'TTS_ON' : 'TTS_OFF';
        ttsToggle.className = `cm-button cm-button--${enabled ? 'primary' : 'outline'}`;
      }
    });
    ttsToggle.title = 'Text-to-Speech — Read key names aloud when pressed. Useful for accessibility and blind testing.';
    settingsContent.appendChild(ttsToggle);

    const settingsSection = document.createElement('section');
    settingsSection.className = 'cm-panel-section';
    settingsSection.innerHTML = `<div class="cm-panel-title">INPUT_TUNING</div>`;
    settingsSection.appendChild(settingsContent);

    const reportSection = document.createElement('section');
    reportSection.className = 'cm-panel-section';
    reportSection.innerHTML = `<div class="cm-panel-title">REPORTING</div>`;
    const reportGrid = document.createElement('div');
    reportGrid.className = 'cm-control-grid cm-control-grid--two';
    const exportJsonBtn = createButton({
      label: 'EXPORT_JSON', type: 'outline', onClick: () => showInterstitialAd(() => {
        try {
          exportService.exportFile(buildExportPayload(), `kb_session_${Date.now()}`, 'json');
          analyticsService.trackEvent('export_json');
        } catch (err) {
          console.error('Export JSON Error:', err);
          alert('Error during JSON export. See console for details.');
        }
      }, { bypassAd: true })
    });
    const exportCsvBtn = createButton({
      label: 'EXPORT_CSV', type: 'outline', onClick: () => showInterstitialAd(() => {
        try {
          exportService.exportCSVReport(buildExportPayload(), `kb_report_${Date.now()}`);
          analyticsService.trackEvent('export_csv');
        } catch (err) {
          console.error('Export CSV Error:', err);
          alert('Error during CSV export. See console for details.');
        }
      }, { bypassAd: true })
    });
    const printBtn = createButton({
      label: 'PRINT_REPORT', type: 'outline', onClick: () => showInterstitialAd(() => {
        try {
          exportService.printReport(buildReportPayload());
          analyticsService.trackEvent('export_print');
        } catch (err) {
          console.error('Print Report Error:', err);
          alert('Error generating print report. See console for details.');
        }
      }, { bypassAd: true })
    });
    const exportImageBtn = createButton({
      label: 'EXPORT_IMAGE', type: 'outline', onClick: () => showInterstitialAd(() => {
        try {
          exportService.exportImageReport(buildReportPayload(), `kb_report_${Date.now()}`);
          analyticsService.trackEvent('export_image');
        } catch (err) {
          console.error('Export Image Error:', err);
          alert('Error generating image report. See console for details.');
        }
      }, { bypassAd: true })
    });

    exportJsonBtn.title = 'Export JSON — Save full session data (timestamps, metrics, event log) as a JSON file for analysis or comparison.';
    exportCsvBtn.title = 'Export CSV — Save session metrics as a CSV spreadsheet compatible with Excel and Google Sheets.';
    printBtn.title = 'Print Report — Generate a formatted print-friendly report of your session metrics and diagnostics.';
    exportImageBtn.title = 'Export Image — Save a visual screenshot of your keyboard session as a PNG image.';
    reportGrid.appendChild(exportJsonBtn);
    reportGrid.appendChild(exportCsvBtn);
    reportGrid.appendChild(printBtn);
    reportGrid.appendChild(exportImageBtn);
    reportSection.appendChild(reportGrid);

    const adSection = document.createElement('section');
    adSection.className = 'cm-panel-section';
    adSection.innerHTML = `<div class="cm-panel-title">SPONSORED</div>`;
    const sidebarAd = createAdSlot({ id: 'sidebar', width: '300px', height: '250px', label: 'ADVERTISEMENT' });
    adSection.appendChild(sidebarAd);

    const typingSection = document.createElement('section');
    typingSection.className = 'cm-panel-section';
    typingSection.appendChild(createTypingTestWidget());

    const rightPanelSections = [
      controlSection,
      testSection,
      typingSection,
      diagSection,
      settingsSection,
      reportSection,
      adSection
    ];
    rightPanelSections.forEach((section) => rightPanel.appendChild(section));

    workspace.appendChild(leftPanel);
    workspace.appendChild(stage);
    workspace.appendChild(rightPanel);
    dashboard.appendChild(workspace);

    // Right Panel Scroll Hint
    const panelScrollHint = document.createElement('div');
    panelScrollHint.className = 'cm-panel-scroll-hint';
    panelScrollHint.innerHTML = `
      <div class="cm-panel-scroll-hint__text">${t('PANEL_SCROLL_HINT', 'Scroll for Tools')}</div>
      <svg class="cm-panel-scroll-hint__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="6 9 12 15 18 9"></polyline>
      </svg>
    `;
    rightPanel.appendChild(panelScrollHint);

    // Fade hint gently when right panel actually scrolls
    rightPanel.addEventListener('scroll', () => {
      if (rightPanel.scrollTop > 20) {
        panelScrollHint.classList.add('is-hidden');
      } else {
        panelScrollHint.classList.remove('is-hidden');
      }
    }, { passive: true });

    // ESC exit hint (only visible in focus mode)
    const escHint = document.createElement('button');
    escHint.className = 'cm-focus-esc-hint';
    escHint.innerHTML = '<span class="cm-focus-esc-hint__key">ESC</span> <span>PRESS TO EXIT FOCUS MODE</span>';
    escHint.addEventListener('click', () => setFocusMode(false));
    stage.insertBefore(escHint, stage.firstChild);

    // 4. Focus Guard Overlay
    const focusGuard = document.createElement('div');
    focusGuard.className = 'cm-focus-guard';
    focusGuard.innerHTML = `
      <div class="cm-focus-guard__content">
        <div class="cm-focus-guard__icon">!</div>
        <div class="cm-focus-guard__title">APP_PAUSED</div>
        <div class="cm-focus-guard__text">CLICK_TO_RESUME_INPUT_DETECTION</div>
      </div>
    `;
    dashboard.appendChild(focusGuard);

    app.appendChild(dashboard);

    // Wire up scroll listener for fade-out and pausing input
    document.addEventListener('scroll', () => {
      // Because .cm-dashboard is 100vh and the body scrolls, we track window.scrollY
      const isScrolled = window.scrollY > 40;

      // Pause keyboard detection when scrolled out of view to allow native navigation
      if (keyboardService) {
        keyboardService.isPaused = isScrolled;
      }
    }, { passive: true });

    // Wire up Focus Guard
    const updateFocusState = () => {
      const isFocused = document.hasFocus();
      focusGuard.classList.toggle('is-visible', !isFocused);
      if (focusStatusEl) {
        focusStatusEl.textContent = isFocused ? 'FOCUS: ACTIVE' : 'FOCUS: PAUSED';
        focusStatusEl.classList.toggle('is-inactive', !isFocused);
      }
      if (isFocused) {
        eventLog.addLog('INPUT_RESUMED');
      } else {
        eventLog.addLog('INPUT_PAUSED: FOCUS_LOST');
      }
    };

    // Responsive Scaling
    const updateScaling = () => {
      const canvas = document.querySelector('.cm-keyboard-canvas');
      const frame = document.querySelector('.cm-keyboard-frame');
      if (!canvas || !frame || !stage) return;

      const bounds = stage.getBoundingClientRect();
      const paddingX = 16;
      const paddingY = 12;
      const toolbarHeight = kbToolbar ? kbToolbar.offsetHeight : 0;
      const availableWidth = bounds.width - paddingX;
      const availableHeight = bounds.height - paddingY - toolbarHeight;

      const frameWidth = frame.offsetWidth;
      const frameHeight = frame.offsetHeight;
      const scaleCaps = {
        full: 1.85, // Increased from 1.6
        tkl: 1.9,   // Increased from 1.7
        '75': 1.95, // Increased from 1.75
        '65': 2.0,  // Increased from 1.8
        '60': 2.1   // Increased from 1.9
      };
      const maxScale = scaleCaps[currentLayout] || 1.85;

      const scaleX = availableWidth / frameWidth;
      const scaleY = availableHeight / frameHeight;
      const scale = Math.min(maxScale, scaleX, scaleY);

      canvas.style.transform = `scale(${scale})`;

      // Ensure the stage doesn't stretch to match the tallest grid column (right panel).
      // This is set via JS as well as CSS for robustness against caching issues.
      stage.style.alignSelf = 'start';

      // CSS transform: scale() shrinks the visual size but NOT the DOM layout footprint.
      // Compensate by pulling subsequent elements up by the exact amount of phantom space.
      if (scale < 1) {
        const phantomHeight = canvas.offsetHeight * (1 - scale);
        canvas.style.marginBottom = `-${phantomHeight}px`;
      } else {
        canvas.style.marginBottom = '0px';
      }
    };

    window.addEventListener('resize', updateScaling);
    setTimeout(updateScaling, 100);

    window.addEventListener('focus', updateFocusState);
    window.addEventListener('blur', updateFocusState);
    updateFocusState();

    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && document.body.classList.contains('cm-focus-mode')) {
        setFocusMode(false);
      }
    });

    // Wire up dynamic settings
    const chatterRange = settingsContent.querySelector('#chatter-range');
    const chatterVal = settingsContent.querySelector('#chatter-val');
    const chatterReset = settingsContent.querySelector('#chatter-reset');

    const updateChatter = (val) => {
      chatterRange.value = val;
      chatterVal.textContent = val;
      if (chatterService && chatterService.setThreshold) {
        chatterService.setThreshold(parseInt(val));
      }
    };

    chatterRange.addEventListener('input', (e) => updateChatter(e.target.value));
    if (chatterReset) {
      chatterReset.addEventListener('click', () => updateChatter(20));
    }

    /**
     * Visual Keyboard Rendering
     */
    console.log('8. Rendering keyboard blocks...');
    const keyNodes = {};

    const setKeyLabel = (node, label) => {
      const labelEl = node.querySelector('.cm-key__label');
      if (labelEl) {
        labelEl.textContent = label;
      }
    };

    const resolveLabel = (keyData) => {
      if (currentOs === 'mac' && keyData.labelMac) return keyData.labelMac;
      if (currentOs === 'win' && keyData.labelWin) return keyData.labelWin;
      return keyData.label;
    };

    const clearKeyNodes = () => {
      Object.keys(keyNodes).forEach((code) => delete keyNodes[code]);
    };

    const renderBlock = (mountId, keys) => {
      const mount = document.getElementById(mountId);
      if (!mount) return;
      mount.textContent = '';
      if (mountId !== 'keyboard-mount') {
        mount.style.display = keys.length ? 'grid' : 'none';
      }
      keys.forEach(keyData => {
        if (keyData.type === 'spacer') {
          const spacer = document.createElement('div');
          spacer.className = 'cm-spacer';
          const span = keyData.span || 1;
          spacer.style.gridColumn = `span ${mountId === 'keyboard-mount' ? Math.round(span * 4) : Math.round(span)}`;
          if (keyData.row) spacer.dataset.row = keyData.row;
          mount.appendChild(spacer);
          return;
        }

        const keyNode = createKeyboardKey({ code: keyData.code, label: resolveLabel(keyData) });
        keyNode.dataset.baseLabel = keyData.label;
        if (keyData.labelWin || keyData.labelMac) {
          keyNode.dataset.labelWin = keyData.labelWin || keyData.label;
          keyNode.dataset.labelMac = keyData.labelMac || keyData.label;
        }
        if (keyData.row) keyNode.dataset.row = keyData.row;
        if (keyData.span) {
          const span = keyData.span;
          if (mountId === 'keyboard-mount') {
            keyNode.style.gridColumn = `span ${Math.round(span * 4)}`;
          } else {
            keyNode.style.gridColumn = `span ${Math.round(span)}`;
          }
          keyNode.style.width = '100%';
          keyNode.style.height = '100%';
        } else if (mountId === 'keyboard-mount') {
          keyNode.style.gridColumn = 'span 4';
        }

        if (keyData.rowSpan) {
          keyNode.style.gridRow = `span ${keyData.rowSpan}`;
        }
        mount.appendChild(keyNode);
        keyNodes[keyData.code] = keyNode;
      });
    };

    // 60-column grid for precise spacing (1u = 4 columns, 15u total width)
    const mainRows = [
      // Row 1 (Function Row) - 15u total
      [
        { code: 'Escape', label: 'ESC', span: 1, row: 1 },
        { type: 'spacer', span: 1, row: 1 },
        { code: 'F1', label: 'F1', span: 1, row: 1 }, { code: 'F2', label: 'F2', span: 1, row: 1 }, { code: 'F3', label: 'F3', span: 1, row: 1 }, { code: 'F4', label: 'F4', span: 1, row: 1 },
        { type: 'spacer', span: 0.5, row: 1 },
        { code: 'F5', label: 'F5', span: 1, row: 1 }, { code: 'F6', label: 'F6', span: 1, row: 1 }, { code: 'F7', label: 'F7', span: 1, row: 1 }, { code: 'F8', label: 'F8', span: 1, row: 1 },
        { type: 'spacer', span: 0.5, row: 1 },
        { code: 'F9', label: 'F9', span: 1, row: 1 }, { code: 'F10', label: 'F10', span: 1, row: 1 }, { code: 'F11', label: 'F11', span: 1, row: 1 }, { code: 'F12', label: 'F12', span: 1, row: 1 },
      ],

      // Row 2 (Numbers) - 15u total
      [
        { code: 'Backquote', label: '`', span: 1, row: 2 }, { code: 'Digit1', label: '1', span: 1, row: 2 }, { code: 'Digit2', label: '2', span: 1, row: 2 }, { code: 'Digit3', label: '3', span: 1, row: 2 }, { code: 'Digit4', label: '4', span: 1, row: 2 }, { code: 'Digit5', label: '5', span: 1, row: 2 }, { code: 'Digit6', label: '6', span: 1, row: 2 }, { code: 'Digit7', label: '7', span: 1, row: 2 }, { code: 'Digit8', label: '8', span: 1, row: 2 }, { code: 'Digit9', label: '9', span: 1, row: 2 }, { code: 'Digit0', label: '0', span: 1, row: 2 }, { code: 'Minus', label: '-', span: 1, row: 2 }, { code: 'Equal', label: '=', span: 1, row: 2 }, { code: 'Backspace', label: 'BKSP', labelMac: 'DEL', span: 2, row: 2 },
      ],

      // Row 3 (QWERTY) - 15u total
      [
        { code: 'Tab', label: 'TAB', span: 1.5, row: 3 }, { code: 'KeyQ', label: 'Q', span: 1, row: 3 }, { code: 'KeyW', label: 'W', span: 1, row: 3 }, { code: 'KeyE', label: 'E', span: 1, row: 3 }, { code: 'KeyR', label: 'R', span: 1, row: 3 }, { code: 'KeyT', label: 'T', span: 1, row: 3 }, { code: 'KeyY', label: 'Y', span: 1, row: 3 }, { code: 'KeyU', label: 'U', span: 1, row: 3 }, { code: 'KeyI', label: 'I', span: 1, row: 3 }, { code: 'KeyO', label: 'O', span: 1, row: 3 }, { code: 'KeyP', label: 'P', span: 1, row: 3 }, { code: 'BracketLeft', label: '[', span: 1, row: 3 }, { code: 'BracketRight', label: ']', span: 1, row: 3 }, { code: 'Backslash', label: '\\', span: 1.5, row: 3 },
      ],

      // Row 4 (ASDF) - 15u total
      [
        { code: 'CapsLock', label: 'CAPS', span: 1.75, row: 4 }, { code: 'KeyA', label: 'A', span: 1, row: 4 }, { code: 'KeyS', label: 'S', span: 1, row: 4 }, { code: 'KeyD', label: 'D', span: 1, row: 4 }, { code: 'KeyF', label: 'F', span: 1, row: 4 }, { code: 'KeyG', label: 'G', span: 1, row: 4 }, { code: 'KeyH', label: 'H', span: 1, row: 4 }, { code: 'KeyJ', label: 'J', span: 1, row: 4 }, { code: 'KeyK', label: 'K', span: 1, row: 4 }, { code: 'KeyL', label: 'L', span: 1, row: 4 }, { code: 'Semicolon', label: ';', span: 1, row: 4 }, { code: 'Quote', label: '\'', span: 1, row: 4 }, { code: 'Enter', label: 'ENTER', labelMac: 'RETURN', span: 2.25, row: 4 },
      ],

      // Row 5 (ZXCV) - 15u total
      [
        { code: 'ShiftLeft', label: 'SHIFT', span: 2.25, row: 5 }, { code: 'KeyZ', label: 'Z', span: 1, row: 5 }, { code: 'KeyX', label: 'X', span: 1, row: 5 }, { code: 'KeyC', label: 'C', span: 1, row: 5 }, { code: 'KeyV', label: 'V', span: 1, row: 5 }, { code: 'KeyB', label: 'B', span: 1, row: 5 }, { code: 'KeyN', label: 'N', span: 1, row: 5 }, { code: 'KeyM', label: 'M', span: 1, row: 5 }, { code: 'Comma', label: ',', span: 1, row: 5 }, { code: 'Period', label: '.', span: 1, row: 5 }, { code: 'Slash', label: '/', span: 1, row: 5 }, { code: 'ShiftRight', label: 'SHIFT', span: 2.75, row: 5 },
      ],

      // Row 6 (Bottom Row) - 15u total
      [
        { code: 'ControlLeft', label: 'CTRL', span: 1.25, row: 6 },
        { code: 'MetaLeft', label: 'WIN', labelWin: 'WIN', labelMac: 'CMD', span: 1.25, row: 6 },
        { code: 'AltLeft', label: 'ALT', labelWin: 'ALT', labelMac: 'OPT', span: 1.25, row: 6 },
        { code: 'Space', label: 'SPACE', span: 6.25, row: 6 },
        { code: 'AltRight', label: 'ALT', labelWin: 'ALT', labelMac: 'OPT', span: 1.25, row: 6 },
        { code: 'MetaRight', label: 'WIN', labelWin: 'WIN', labelMac: 'CMD', span: 1.25, row: 6 },
        { code: 'ContextMenu', label: 'MENU', labelMac: 'FN', span: 1.25, row: 6 },
        { code: 'ControlRight', label: 'CTRL', span: 1.25, row: 6 }
      ]
    ];

    const navKeys = [
      { code: 'PrintScreen', label: 'PRT', span: 1 }, { code: 'ScrollLock', label: 'SCR', span: 1 }, { code: 'Pause', label: 'PAU', span: 1 },
      { code: 'Insert', label: 'INS', span: 1 }, { code: 'Home', label: 'HM', span: 1 }, { code: 'PageUp', label: 'PU', span: 1 },
      { code: 'Delete', label: 'DEL', span: 1 }, { code: 'End', label: 'END', span: 1 }, { code: 'PageDown', label: 'PD', span: 1 },
      { type: 'spacer', span: 3 },
      { type: 'spacer', span: 1 }, { code: 'ArrowUp', label: 'UP', span: 1 }, { type: 'spacer', span: 1 },
      { code: 'ArrowLeft', label: 'LEFT', span: 1 }, { code: 'ArrowDown', label: 'DOWN', span: 1 }, { code: 'ArrowRight', label: 'RIGHT', span: 1 }
    ];

    const navCompactKeys = [
      { code: 'Insert', label: 'INS', span: 1 }, { code: 'Home', label: 'HM', span: 1 }, { code: 'PageUp', label: 'PU', span: 1 },
      { code: 'Delete', label: 'DEL', span: 1 }, { code: 'End', label: 'END', span: 1 }, { code: 'PageDown', label: 'PD', span: 1 },
      { type: 'spacer', span: 3 },
      { type: 'spacer', span: 1 }, { code: 'ArrowUp', label: 'UP', span: 1 }, { type: 'spacer', span: 1 },
      { code: 'ArrowLeft', label: 'LEFT', span: 1 }, { code: 'ArrowDown', label: 'DOWN', span: 1 }, { code: 'ArrowRight', label: 'RIGHT', span: 1 }
    ];

    const navArrowKeys = [
      { type: 'spacer', span: 3 },
      { type: 'spacer', span: 1 }, { code: 'ArrowUp', label: 'UP', span: 1 }, { type: 'spacer', span: 1 },
      { code: 'ArrowLeft', label: 'LEFT', span: 1 }, { code: 'ArrowDown', label: 'DOWN', span: 1 }, { code: 'ArrowRight', label: 'RIGHT', span: 1 }
    ];

    const numpadKeys = [
      { type: 'spacer', span: 4 },
      { code: 'NumLock', label: 'NL', span: 1 }, { code: 'NumpadDivide', label: '/', span: 1 }, { code: 'NumpadMultiply', label: '*', span: 1 }, { code: 'NumpadSubtract', label: '-', span: 1 },
      { code: 'Numpad7', label: '7', span: 1 }, { code: 'Numpad8', label: '8', span: 1 }, { code: 'Numpad9', label: '9', span: 1 }, { code: 'NumpadAdd', label: '+', span: 1, rowSpan: 2 },
      { code: 'Numpad4', label: '4', span: 1 }, { code: 'Numpad5', label: '5', span: 1 }, { code: 'Numpad6', label: '6', span: 1 }, // no spacer here, NumpadAdd spans down
      { code: 'Numpad1', label: '1', span: 1 }, { code: 'Numpad2', label: '2', span: 1 }, { code: 'Numpad3', label: '3', span: 1 }, { code: 'NumpadEnter', label: 'ENT', span: 1, rowSpan: 2 },
      { code: 'Numpad0', label: '0', span: 2 }, { code: 'NumpadDecimal', label: '.', span: 1 } // no spacer here, NumpadEnter spans down
    ];

    const mainKeys = mainRows.flat();
    const mainKeysNoFn = mainKeys.filter((key) => key.row !== 1);

    const layoutPresets = {
      full: { label: 'FULL', main: mainKeys, nav: navKeys, numpad: numpadKeys },
      tkl: { label: 'TKL', main: mainKeys, nav: navKeys, numpad: [] },
      '75': { label: '75', main: mainKeys, nav: navCompactKeys, numpad: [] },
      '65': { label: '65', main: mainKeysNoFn, nav: navArrowKeys, numpad: [] },
      '60': { label: '60', main: mainKeysNoFn, nav: [], numpad: [] }
    };

    const applyOsLabels = () => {
      Object.values(keyNodes).forEach((node) => {
        const label = currentOs === 'mac' ? node.dataset.labelMac : node.dataset.labelWin;
        if (label) setKeyLabel(node, label);
      });
    };

    const applyStandardLayout = () => {
      const map = standardLayoutMaps[currentStandard] || {};
      Object.entries(keyNodes).forEach(([code, node]) => {
        const baseLabel = node.dataset.baseLabel;
        if (!baseLabel) return;
        const mapped = map[code] || baseLabel;
        setKeyLabel(node, mapped);
      });
      applyOsLabels();
    };

    setStandardLayout = (layoutKey) => {
      currentStandard = layoutKey;
      if (standardSelect) standardSelect.value = layoutKey;
      applyStandardLayout();
      eventLog.addLog(`STANDARD_LAYOUT: ${layoutKey.toUpperCase()}`);
    };

    const renderLayout = (layoutKey) => {
      const preset = layoutPresets[layoutKey] || layoutPresets.full;
      currentLayout = layoutKey;
      clearKeyNodes();
      renderBlock('keyboard-mount', preset.main);
      renderBlock('nav-mount', preset.nav);
      renderBlock('numpad-mount', preset.numpad);
      applyStandardLayout();
      setTimeout(updateScaling, 50);
    };

    renderLayout(currentLayout);

    /**
     * Wire Up Services
     */
    const lastDownEvents = [];

    keyboardService.addEventListener('keychange', (e) => {
      const { type, code, key, repeat, activeCount, maxCount, originalEvent } = e.detail;

      // Track Chord Splitting
      if (type === 'down' && !repeat) {
        lastDownEvents.push({ code, timestamp: originalEvent.timeStamp });
        if (lastDownEvents.length > 5) lastDownEvents.shift();
        accuracyService.detectChordSplit(lastDownEvents);
      }

      // 1. Update Metrics
      activeKeysMetric.querySelector('.cm-metric-card__value').textContent = activeCount;
      maxKeysMetric.querySelector('.cm-metric-card__value').textContent = maxCount;
      if (type === 'down' && !repeat) {
        totalPressCount++;
        totalPressesMetric.querySelector('.cm-metric-card__value').textContent = totalPressCount;
      }
      if (lastKeyEl && type === 'down') {
        lastKeyEl.textContent = `LAST:\u00A0\u00A0${code}`;
      }

      // 2. Visual Keyboard Update
      if (keyNodes[code]) {
        keyNodes[code].className = `cm-key cm-key--${type === 'down' ? 'active' : 'history'}`;
      }

      // 3. Log Event
      if (!repeat) {
        eventLog.addLog(`KEY_${type.toUpperCase()}: ${code} [${key}]`);
      }

      // 4. Record Accuracy, Chatter, Heatmap & Stuck Key
      accuracyService.recordEvent(originalEvent.timeStamp);
      chatterService.recordEvent(e);
      lockKeyStateService.updateFromEvent(originalEvent);

      if (type === 'down') {
        heatmapService.recordPress(code, activeCount);
        if (!repeat) {
          stuckKeyService.recordKeyDown(code);
          layoutWizardService.recordKey(code);
          apmService.registerAction(); // Log for APM/CPS
        }
        if (repeat) repeatRateService.recordKeyDown(code, originalEvent.timeStamp);
      } else {
        stuckKeyService.recordKeyUp(code);
      }

      // 5. Audio Feedback
      if (type === 'down' && !repeat) {
        audioService.playKeySound(code, originalEvent.location);
        if (audioService.ttsEnabled) audioService.speak(key);
      }

      // 6. Typing Speed Test
      if (type === 'down' && !repeat) {
        typingTestService.processKey(key);
      }
    });

    let lastGraphUpdate = 0;
    accuracyService.addEventListener('accuracyupdate', (e) => {
      const { hz, peak } = e.detail;
      lastPeak = peak;
      pollingMetric.querySelector('.cm-metric-card__value').textContent = hz;
      latencyMetric.querySelector('.cm-metric-card__value').textContent = peak.toFixed(1);

      // Update APM & CPS
      const apmMetrics = apmService.getMetrics();
      apmMetric.querySelector('.cm-metric-card__value').textContent = apmMetrics.apm;
      cpsMetric.querySelector('.cm-metric-card__value').textContent = apmMetrics.cps;

      if (Date.now() - lastGraphUpdate > 100) {
        pollingSpark.addData(hz);
        latencySpark.addData(peak);
        apmSpark.addData(apmMetrics.apm);
        lastGraphUpdate = Date.now();
      }

      // Add real-time visual feedback to the polling metric based on stability
      const pollingEl = pollingMetric.querySelector('.cm-metric-card__value');
      if (hz > 500) {
        pollingEl.style.color = 'var(--color-primary)';
        pollingEl.style.textShadow = 'var(--shadow-glow)';
      } else {
        pollingEl.style.color = 'var(--color-text)';
        pollingEl.style.textShadow = 'none';
      }
    });

    chatterService.addEventListener('chatterdetected', (e) => {
      const { code, interval } = e.detail;
      const diagMount = document.getElementById('diag-mount');
      if (diagMount) {
        diagMount.innerHTML = `<span class="cm-diag-status__dot cm-diag-status__dot--error"></span><span class="cm-diag-status__label">CHATTER_DETECTED: ${code} (${interval}ms)</span>`;
        diagMount.classList.add('cm-diag-status--error');
        diagMount.classList.remove('cm-diag-status--accent');
      }
      chatterCount += 1;
      updateDiagCounts();

      if (keyNodes[code]) {
        keyNodes[code].classList.add('cm-key--chatter');
      }
    });

    keyboardService.addEventListener('clear', () => {
      activeKeysMetric.querySelector('.cm-metric-card__value').textContent = '0';
      Object.values(keyNodes).forEach(node => {
        node.className = 'cm-key cm-key--idle';
      });
    });

    /**
     * Wizard & Heatmap Logic
     */
    let currentWizardModal = null;

    nkroService.addEventListener('wizardstep', (e) => {
      const { step, total, zone } = e.detail;

      if (currentWizardModal) currentWizardModal.remove();

      const content = document.createElement('div');
      content.innerHTML = `
    <p style="margin-bottom: var(--spacing-md)">STEP ${step} OF ${total}</p>
    <h2 style="color: var(--color-primary); margin-bottom: var(--spacing-sm)">${zone.name}</h2>
    <p>${zone.description}</p>
    <div id="wizard-progress" style="margin-top: var(--spacing-lg); font-size: 24px; color: var(--color-primary)">0 / ${zone.keys.length}</div>
  `;

      currentWizardModal = createModal({
        title: 'NKRO_WIZARD',
        content: content,
        footer: createButton({
          label: 'NEXT_STEP', type: 'primary', onClick: () => {
            const successCount = parseInt(document.getElementById('wizard-progress').textContent.split(' / ')[0]);
            nkroService.nextStep(successCount);
          }
        })
      });

      currentWizardModal.open();
    });

    // Update wizard progress live
    keyboardService.addEventListener('keychange', () => {
      if (!nkroService.isActive) return;
      const zone = nkroService.ZONES[nkroService.currentStepIndex];
      const activeInZone = zone.keys.filter(k => keyboardService.activeKeys.has(k)).length;
      const progressEl = document.getElementById('wizard-progress');
      if (progressEl) {
        const currentMax = parseInt(progressEl.textContent.split(' / ')[0]);
        if (activeInZone > currentMax) {
          progressEl.textContent = `${activeInZone} / ${zone.keys.length}`;
        }
      }
    });

    nkroService.addEventListener('wizardcomplete', (e) => {
      if (currentWizardModal) currentWizardModal.remove();

      const { results } = e.detail;
      const passed = results.every(r => r.pass);

      analyticsService.trackEvent('nkro_test_complete', { passed, resultCount: results.length });

      const content = document.createElement('div');
      content.innerHTML = `
    <h2 style="color: var(--color-${passed ? 'primary' : 'error'})">${passed ? 'TEST_PASSED' : 'TEST_INCOMPLETE'}</h2>
    <ul style="list-style: none; padding: 0; margin-top: var(--spacing-md)">
      ${results.map(r => `<li>${r.zone}: ${r.actual}/${r.expected} ${r.pass ? 'PASS' : 'FAIL'}</li>`).join('')}
    </ul>
  `;

      createModal({
        title: 'WIZARD_COMPLETE',
        content: content,
        footer: createButton({ label: 'CLOSE', type: 'outline', onClick: () => location.reload() })
      }).open();
    });

    layoutWizardService.addEventListener('wizardstep', (e) => {
      const { step, total, instruction } = e.detail;
      if (currentWizardModal) currentWizardModal.remove();

      const content = document.createElement('div');
      content.innerHTML = `
        <div style="display:flex;align-items:center;gap:12px;margin-bottom:16px;">
          <div style="background:var(--color-primary);color:#000;padding:6px 14px;border-radius:6px;font-weight:700;font-size:14px;">STEP ${step}/${total}</div>
          <div style="flex:1;height:6px;background:rgba(255,255,255,0.1);border-radius:3px;overflow:hidden;">
            <div style="width:${(step / total) * 100}%;height:100%;background:var(--color-primary);border-radius:3px;transition:width 0.3s ease;"></div>
          </div>
        </div>
        <h2 style="color: var(--color-primary); margin-bottom: var(--spacing-sm); font-size: 1.4rem; letter-spacing: 1px;">${instruction.replace(/_/g, ' ')}</h2>
        <p style="margin-top: var(--spacing-md); color: var(--color-text-dim); font-size: 14px;">Press the requested key to detect layout differences.</p>
      `;

      currentWizardModal = createModal({
        title: 'LAYOUT_WIZARD',
        content: content,
        footer: createButton({
          label: 'CANCEL', type: 'outline', onClick: () => {
            layoutWizardService.isActive = false;
            if (currentWizardModal) currentWizardModal.remove();
          }
        })
      });
      currentWizardModal.open();
    });

    layoutWizardService.addEventListener('wizardcomplete', (e) => {
      if (currentWizardModal) currentWizardModal.remove();
      const { layout } = e.detail;

      analyticsService.trackEvent('layout_wizard_complete', { detectedLayout: layout });

      const content = document.createElement('div');
      content.innerHTML = `
        <div style="text-align:center;margin-bottom:16px;">
          <div style="display:inline-flex;align-items:center;justify-content:center;width:50px;height:50px;border-radius:50%;background:rgba(0,255,157,0.15);border:2px solid var(--color-primary);font-size:24px;color:var(--color-primary);">✓</div>
        </div>
        <h2 style="color: var(--color-primary); text-align:center;">LAYOUT DETECTED</h2>
        <p style="margin-top: var(--spacing-md); text-align:center; color: var(--color-text-dim);">Based on your key presses, your keyboard layout is:</p>
        <h1 style="color: var(--color-text); margin-top: var(--spacing-md); text-align: center; font-size: 2.5rem; letter-spacing: 3px;">${layout}</h1>
      `;

      createModal({
        title: 'WIZARD_COMPLETE',
        content: content,
        footer: createButton({
          label: 'CLOSE & APPLY', type: 'primary', onClick: () => {
            // You could optionally set a standard layout based on ISO vs ANSI here
            if (currentWizardModal) currentWizardModal.remove();
          }
        })
      }).open();
    });

    heatmapService.addEventListener('heatmapupdate', (e) => {
      const { code, count, intensity } = e.detail;
      const keyNode = keyNodes[code];
      if (keyNode) {
        // Update press count badge
        const countEl = keyNode.querySelector('.cm-key__count');
        if (countEl) countEl.textContent = count;

        if (document.body.classList.contains('cm-heatmap-view') && window.cytificGetHeatmapColor) {
          keyNode.style.setProperty('background-color', window.cytificGetHeatmapColor(count), 'important');
          keyNode.style.setProperty('box-shadow', `0 0 15px ${window.cytificGetHeatmapColor(count)}`, 'important');
        } else {
          // Subtly increase glow based on usage (only when not actively pressed)
          if (!keyNode.classList.contains('cm-key--active')) {
            const alpha = Math.min(0.8, 0.2 + (intensity * 0.1));
            keyNode.style.boxShadow = `0 0 10px rgba(0, 255, 157, ${alpha})`;
          }
        }
      }
    });

    lockKeyStateService.addEventListener('lockchange', (e) => {
      const { key, state } = e.detail;
      const map = { CapsLock: 'caps', NumLock: 'num', ScrollLock: 'scroll' };
      const el = document.getElementById(`lock-${map[key]}`);
      if (el) {
        el.classList.toggle('is-active', state);
      }
    });

    stuckKeyService.addEventListener('stuckdetected', (e) => {
      const { code } = e.detail;
      const diagMount = document.getElementById('diag-mount');
      if (diagMount) {
        diagMount.innerHTML = `<span class="cm-diag-status__dot cm-diag-status__dot--error"></span><span class="cm-diag-status__label">STUCK_KEY_DETECTED: ${code}</span>`;
        diagMount.classList.add('cm-diag-status--error');
        diagMount.classList.remove('cm-diag-status--accent');
      }
      stuckCount += 1;
      updateDiagCounts();
      if (keyNodes[code]) {
        keyNodes[code].classList.add('cm-key--stuck');
      }
      audioService.playKeySound(code); // Alert sound
    });

    stuckKeyService.addEventListener('stuckresolved', (e) => {
      const { code } = e.detail;
      if (keyNodes[code]) {
        keyNodes[code].classList.remove('cm-key--stuck');
      }
      stuckCount = Math.max(0, stuckCount - 1);
      updateDiagCounts();
      const diagMount = document.getElementById('diag-mount');
      if (diagMount && diagMount.textContent.includes(code)) {
        diagMount.innerHTML = `<span class="cm-diag-status__dot"></span><span class="cm-diag-status__label">NO_ISSUES_DETECTED</span>`;
        diagMount.classList.remove('cm-diag-status--error', 'cm-diag-status--accent');
      }
    });

    repeatRateService.addEventListener('repeatmeasured', (e) => {
      const { code, delay, rate } = e.detail;
      eventLog.addLog(`REPEAT_RESULT: DELAY=${delay}ms RATE=${rate}ms`);
      chatterService.calibrateRepeat(delay, rate);
      lastRepeat = { code, delay, rate };
      const diagMount = document.getElementById('diag-mount');
      if (diagMount) {
        diagMount.innerHTML = `<span class="cm-diag-status__dot cm-diag-status__dot--accent"></span><span class="cm-diag-status__label">REPEAT_READY: ${code} ${delay}ms/${rate}ms</span>`;
        diagMount.classList.add('cm-diag-status--accent');
        diagMount.classList.remove('cm-diag-status--error');
      }
    });

    accuracyService.addEventListener('chordsplit', (e) => {
      const { count } = e.detail;
      eventLog.addLog(`CHORD_SPLIT_DETECTED: [${count}] SIMULTANEOUS_KEYS_SEPARATED`);
      const diagMount = document.getElementById('diag-mount');
      if (diagMount) {
        diagMount.innerHTML = `<span class="cm-diag-status__dot cm-diag-status__dot--accent"></span><span class="cm-diag-status__label">CHORD_SPLIT_DETECTED: ${count}</span>`;
        diagMount.classList.add('cm-diag-status--accent');
        diagMount.classList.remove('cm-diag-status--error');
      }
      chordSplitCount = count;
      updateDiagCounts();
    });



    layoutWizardService.addEventListener('wizardstep', (e) => {
      const { step, total, instruction } = e.detail;
      if (currentWizardModal) currentWizardModal.remove();

      currentWizardModal = createModal({
        title: 'LAYOUT_WIZARD',
        content: `<p>STEP ${step} OF ${total}</p><h2>${instruction}</h2>`,
        onClose: () => { layoutWizardService.isActive = false; }
      });
      currentWizardModal.open();
    });

    layoutWizardService.addEventListener('wizardcomplete', (e) => {
      if (currentWizardModal) currentWizardModal.remove();
      eventLog.addLog(`LAYOUT_DETECTED: ${e.detail.layout}`);
      createModal({
        title: 'WIZARD_COMPLETE',
        content: `<h2>DETECTED_LAYOUT: ${e.detail.layout}</h2>`,
        footer: createButton({ label: 'OK', type: 'primary', onClick: () => location.reload() })
      }).open();
    });
    // ====================================================================
    // BELOW-FOLD: Educational content, ad slots, and footer
    // Appended directly to #app *after* the dashboard for scrollability.
    // Uses the 'app' reference already declared at initApp top scope.
    // ====================================================================
    // ====================================================================
    // VIGNETTE AD: Show interstitial when returning to the tab after 10s
    // ====================================================================
    let lastHiddenTime = 0;
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        lastHiddenTime = Date.now();
      } else {
        if (lastHiddenTime > 0 && Date.now() - lastHiddenTime > 10000) {
          showInterstitialAd(() => {
            console.log('Returned from background.');
          }, { duration: 5 }); // slightly longer countdown for return
        }
        lastHiddenTime = 0;
      }
    });

    const belowFold = createInfoSection();
    document.getElementById('app').appendChild(belowFold);

    // Global Audio Context Unlocker
    // Browsers require a user gesture to start AudioContext
    ['click', 'keydown'].forEach(evt => document.addEventListener(evt, () => {
      if (audioService && !audioService.audioCtx) {
        audioService.init();
      }
    }, { once: true }));

    analyticsService.trackEvent('app_init', {
      layout: currentLayout,
      style: currentStyle,
      os: currentOs
    });

    // Scroll-to-Top Floating Button with Scroll Progress Ring
    const scrollTopBtn = document.createElement('button');
    scrollTopBtn.className = 'cm-scroll-top';
    scrollTopBtn.innerHTML = '<svg viewBox="0 0 24 24"><polyline points="18 15 12 9 6 15"></polyline></svg>';
    scrollTopBtn.title = 'Scroll to top';
    scrollTopBtn.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
    document.body.appendChild(scrollTopBtn);

    window.addEventListener('scroll', () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = docHeight > 0 ? Math.min(100, (scrollTop / docHeight) * 100) : 0;

      scrollTopBtn.style.setProperty('--scroll-progress', scrollPercent);

      if (scrollTop > 300) {
        scrollTopBtn.classList.add('cm-scroll-top--visible');
      } else {
        scrollTopBtn.classList.remove('cm-scroll-top--visible');
      }
    }, { passive: true });

    console.log('initApp completed.');
  } catch (err) {
    console.error('FATAL ERROR during initApp:', err);
    document.body.innerHTML = `<div style="color: red; padding: 20px;"><h1>FATAL_ERROR</h1><pre>${err.stack}</pre></div>`;
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initApp);
} else {
  initApp();
}
